import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, CheckSquare, Image as ImageIcon, Info, AlertCircle,
  CheckCircle2, Table, LayoutGrid, FileText, Eye, Loader2, Sparkles,
  Smile, Link as LinkIcon, Undo, Redo, Plus, Type, ChevronDown, AtSign
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/meta";
import { uploadImageAttachment, htmlToMarkdown, markdownToHtml, processBase64ImagesInMarkdown, excelTextToHtmlTable } from "@/lib/markdownUtils";

const EMOJIS = ["👍", "❤️", "🎉", "🚀", "💡", "🔥", "👀", "🙌", "✅", "❌", "⭐", "📌"];

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Add a description…",
  minRows = 6,
  issueId = null,
  pageId = null,
  users = [],
  testid = "rich-text-editor",
}) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [insertOpen, setInsertOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // @ Mention Autocomplete state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionActiveIdx, setMentionActiveIdx] = useState(0);
  const [mentionRange, setMentionRange] = useState(null);
  const [mentionCoords, setMentionCoords] = useState({ top: 40, left: 16 });

  const mentionMatches = (users || [])
    .filter((u) => {
      if (!mentionQuery) return true;
      const q = mentionQuery.toLowerCase();
      const n = (u.name || "").toLowerCase();
      const e = (u.email || "").toLowerCase();
      return n.includes(q) || e.includes(q) || n.replace(/\s+/g, "").includes(q);
    })
    .slice(0, 8);

  const checkMentionTrigger = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) {
      setMentionOpen(false);
      return;
    }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.slice(0, range.startOffset);
      const match = text.match(/(?:^|\s)@([\w.-]*)$/);
      if (match) {
        const q = match[1] || "";
        const triggerIndex = text.length - match[0].length + (match[0].startsWith("@") ? 0 : 1);
        
        try {
          const rect = range.getBoundingClientRect();
          const editorRect = editorRef.current?.getBoundingClientRect();
          if (rect && editorRect) {
            setMentionCoords({
              top: Math.max(20, rect.bottom - editorRect.top + 10),
              left: Math.max(10, Math.min(rect.left - editorRect.left, 280)),
            });
          }
        } catch {
          // fallback coords
        }

        setMentionQuery(q);
        setMentionActiveIdx(0);
        setMentionRange({ node, triggerIndex, length: text.length - triggerIndex });
        setMentionOpen(true);
        return;
      }
    }
    setMentionOpen(false);
  };

  const insertMentionUser = (u) => {
    if (!u) return;
    const nameSlug = (u.name || "user").replace(/\s+/g, "");
    const mentionHtml = `<span class="jira-mention font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5" contenteditable="false" data-user-id="${u.id}">@${nameSlug}</span>&nbsp;`;

    if (editorRef.current) {
      editorRef.current.focus();
      const sel = window.getSelection();
      
      if (mentionRange && mentionRange.node && mentionRange.node.parentNode) {
        const node = mentionRange.node;
        const fullText = node.textContent;
        const before = fullText.slice(0, mentionRange.triggerIndex);
        const after = fullText.slice(mentionRange.triggerIndex + mentionRange.length);
        
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = mentionHtml;
        const frag = document.createDocumentFragment();
        
        if (before) frag.appendChild(document.createTextNode(before));
        while (tempDiv.firstChild) {
          frag.appendChild(tempDiv.firstChild);
        }
        const afterNode = document.createTextNode(after || " ");
        frag.appendChild(afterNode);
        
        node.parentNode.replaceChild(frag, node);
        
        const newRange = document.createRange();
        newRange.setStart(afterNode, after ? 0 : 1);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } else {
        insertHtmlAtCursor(mentionHtml);
      }
    }

    setMentionOpen(false);
    setMentionQuery("");
    setMentionRange(null);
    emitChange();
  };

  const handleEditorKeyDown = (e) => {
    if (mentionOpen && mentionMatches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionActiveIdx((i) => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionActiveIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMentionUser(mentionMatches[mentionActiveIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
  };

  // Sync initial or updated value from props to editor DOM (without overriding active typing)
  useEffect(() => {
    if (editorRef.current && !isFocused) {
      const currentHtml = editorRef.current.innerHTML;
      const expectedHtml = markdownToHtml(value);
      if (!currentHtml || (value && !currentHtml.trim()) || (value && htmlToMarkdown(currentHtml).trim() !== value.trim())) {
        editorRef.current.innerHTML = expectedHtml || `<p><br></p>`;
      }
    }
  }, [value, isFocused]);

  const emitChange = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    const md = htmlToMarkdown(html);
    onChange?.(md);
  };

  const execCmd = (cmd, val = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    emitChange();
  };

  const insertHtmlAtCursor = (htmlSnippet) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const el = document.createElement("div");
      el.innerHTML = htmlSnippet;
      const frag = document.createDocumentFragment();
      let node, lastNode;
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else {
      editorRef.current.innerHTML += htmlSnippet;
    }
    emitChange();
  };

  const insertTable = (rows = 3, cols = 3) => {
    let tableHtml = '<div class="overflow-x-auto my-3"><table class="w-full border-collapse border border-border rounded-lg text-sm bg-background shadow-xs"><thead><tr>';
    for (let c = 1; c <= cols; c++) {
      tableHtml += `<th class="border border-border px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider bg-muted/60 text-muted-foreground">Header ${c}</th>`;
    }
    tableHtml += '</tr></thead><tbody>';
    for (let r = 1; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 1; c <= cols; c++) {
        tableHtml += `<td class="border border-border px-3 py-2 text-sm">Cell ${r}.${c}</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table></div><p><br></p>';
    insertHtmlAtCursor(tableHtml);
    toast.success("Inserted table!");
  };

  const insertImageTag = (url, filename) => {
    const imgHtml = `<div class="my-3 inline-block max-w-full"><img src="${url}" alt="${filename}" class="max-w-md max-h-[400px] rounded-lg border border-border shadow-sm object-contain cursor-pointer transition-transform hover:scale-[1.01]" /></div><p><br></p>`;
    insertHtmlAtCursor(imgHtml);
  };

  const isImageFile = (file) => {
    if (!file) return false;
    const type = (file.type || "").toLowerCase();
    const name = (file.name || "").toLowerCase();
    return type.startsWith("image/") || type.includes("heic") || type.includes("heif") || /\.(heic|heif|png|jpe?g|gif|webp|svg)$/i.test(name);
  };

  const uploadAndInsertFile = async (file) => {
    if (!file || !isImageFile(file)) {
      toast.error("Please select a valid image file");
      return;
    }
    setUploading(true);
    try {
      toast.info("Uploading image to description...");
      const res = await uploadImageAttachment(file, issueId, pageId);
      insertImageTag(res.url, res.filename);
      toast.success("Image attached directly to description!");
    } catch (err) {
      console.error("Failed to upload image:", err);
      toast.error(err?.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files?.length) {
      const file = e.target.files[0];
      await uploadAndInsertFile(file);
      e.target.value = "";
    }
  };

  const handlePaste = async (e) => {
    // 1. Image Files in Clipboard (e.g. screenshot, copied image, HEIC file)
    if (e.clipboardData?.items) {
      for (let i = 0; i < e.clipboardData.items.length; i++) {
        const item = e.clipboardData.items[i];
        if (item.type && (item.type.startsWith("image/") || item.type.includes("heic") || item.type.includes("heif"))) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await uploadAndInsertFile(file);
            return;
          }
        }
      }
    }

    // 2. Excel Tab-Separated Text Paste
    const plainText = e.clipboardData?.getData("text/plain");
    if (plainText && plainText.includes("\t") && plainText.includes("\n")) {
      const tableHtml = excelTextToHtmlTable(plainText);
      if (tableHtml) {
        e.preventDefault();
        insertHtmlAtCursor(tableHtml);
        toast.success("Pasted Excel table!");
        return;
      }
    }

    // 3. HTML Clipboard Content (Confluence / Word / Excel / Web)
    const html = e.clipboardData?.getData("text/html");
    if (html && (html.includes("<table") || html.includes("<img") || html.includes("<h") || html.includes("<ul") || html.includes("<ol") || html.includes("<p>"))) {
      e.preventDefault();
      const md = htmlToMarkdown(html);
      if (md) {
        setUploading(true);
        try {
          const processedMd = await processBase64ImagesInMarkdown(md, issueId, pageId);
          const formattedHtml = markdownToHtml(processedMd);
          insertHtmlAtCursor(formattedHtml);
          toast.success("Pasted formatted content!");
        } catch {
          insertHtmlAtCursor(markdownToHtml(md));
        } finally {
          setUploading(false);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer?.files?.length) {
      const file = e.dataTransfer.files[0];
      if (isImageFile(file)) {
        await uploadAndInsertFile(file);
      }
    }
  };

  const insertCallout = (type = "info") => {
    const meta = {
      info: { title: "INFO", cls: "bg-blue-500/10 border-blue-500/30 text-blue-900 dark:text-blue-200" },
      warning: { title: "WARNING", cls: "bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200" },
      success: { title: "SUCCESS", cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200" },
    }[type] || { title: "NOTE", cls: "bg-muted border-border" };

    const html = `<div class="p-3 my-3 rounded-lg border text-xs font-medium ${meta.cls}"><b>${meta.title}:</b> Add note details here…</div><p><br></p>`;
    insertHtmlAtCursor(html);
  };

  const insertLink = () => {
    const url = prompt("Enter URL:", "https://");
    if (url) {
      execCmd("createLink", url);
    }
  };

  return (
    <div
      className={cn(
        "relative rounded-lg border bg-background transition-all duration-150 overflow-hidden shadow-xs",
        isFocused ? "border-primary/60 ring-2 ring-primary/10" : "border-border hover:border-border/80",
        dragActive ? "border-primary ring-2 ring-primary/20 bg-primary/5" : ""
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={testid}
    >
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*,.heic,.heif,image/heic,image/heif"
        className="hidden"
      />

      {/* Jira-Style Top Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 flex-wrap gap-1 select-none">
        <div className="flex items-center gap-1 flex-wrap text-muted-foreground">
          {/* AI / Sparkles */}
          <button
            type="button"
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors text-primary"
            title="AI Options"
            onClick={() => toast.info("AI assistant is ready to polish your description!")}
          >
            <Sparkles className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-border/60 mx-0.5" />

          {/* Text Style Dropdown (Tt) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setStyleOpen(!styleOpen)}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium hover:bg-accent hover:text-foreground transition-colors"
              title="Text Formatting Style"
            >
              <Type className="w-4 h-4" />
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
            {styleOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 w-36 rounded-md border bg-popover p-1 shadow-lg text-xs space-y-0.5" onMouseLeave={() => setStyleOpen(false)}>
                <button type="button" onClick={() => { execCmd("formatBlock", "p"); setStyleOpen(false); }} className="w-full text-left px-2 py-1 hover:bg-accent rounded">Normal text</button>
                <button type="button" onClick={() => { execCmd("formatBlock", "h1"); setStyleOpen(false); }} className="w-full text-left px-2 py-1 font-bold text-sm hover:bg-accent rounded">Heading 1</button>
                <button type="button" onClick={() => { execCmd("formatBlock", "h2"); setStyleOpen(false); }} className="w-full text-left px-2 py-1 font-semibold text-xs hover:bg-accent rounded">Heading 2</button>
                <button type="button" onClick={() => { execCmd("formatBlock", "h3"); setStyleOpen(false); }} className="w-full text-left px-2 py-1 font-medium text-xs hover:bg-accent rounded">Heading 3</button>
                <button type="button" onClick={() => { execCmd("formatBlock", "pre"); setStyleOpen(false); }} className="w-full text-left px-2 py-1 font-mono text-[11px] hover:bg-accent rounded">Code block</button>
              </div>
            )}
          </div>

          <div className="h-4 w-px bg-border/60 mx-0.5" />

          {/* Bold */}
          <button
            type="button"
            onClick={() => execCmd("bold")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground font-bold transition-colors"
            title="Bold (Ctrl+B)"
          >
            <Bold className="w-4 h-4" />
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() => execCmd("italic")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground italic transition-colors"
            title="Italic (Ctrl+I)"
          >
            <Italic className="w-4 h-4" />
          </button>

          {/* Strikethrough */}
          <button
            type="button"
            onClick={() => execCmd("strikeThrough")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground line-through transition-colors"
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-border/60 mx-0.5" />

          {/* Numbered List */}
          <button
            type="button"
            onClick={() => execCmd("insertOrderedList")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
            title="Numbered list"
          >
            <ListOrdered className="w-4 h-4" />
          </button>

          {/* Bullet List */}
          <button
            type="button"
            onClick={() => execCmd("insertUnorderedList")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
            title="Bullet list"
          >
            <List className="w-4 h-4" />
          </button>

          <div className="h-4 w-px bg-border/60 mx-0.5" />

          {/* Image Upload Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors text-emerald-600 dark:text-emerald-400"
            title="Upload or Drag & Drop Image"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          </button>

          {/* Code Block */}
          <button
            type="button"
            onClick={() => execCmd("formatBlock", "pre")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
            title="Code block"
          >
            <Code className="w-4 h-4" />
          </button>

          {/* Emoji Picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setEmojiOpen(!emojiOpen)}
              className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
              title="Insert Emoji"
            >
              <Smile className="w-4 h-4" />
            </button>
            {emojiOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 p-2 rounded-md border bg-popover shadow-lg grid grid-cols-4 gap-1.5" onMouseLeave={() => setEmojiOpen(false)}>
                {EMOJIS.map((emo) => (
                  <button
                    key={emo}
                    type="button"
                    onClick={() => { insertHtmlAtCursor(`<span>${emo}</span> `); setEmojiOpen(false); }}
                    className="p-1 hover:bg-accent rounded text-base transition-transform hover:scale-125"
                  >
                    {emo}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Callout Box */}
          <button
            type="button"
            onClick={() => insertCallout("info")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors text-blue-500"
            title="Info Panel"
          >
            <Info className="w-4 h-4" />
          </button>

          {/* Table Button */}
          <button
            type="button"
            onClick={() => insertTable(3, 3)}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors text-purple-600 dark:text-purple-400"
            title="Insert Table (3x3)"
            data-testid="editor-table-btn"
          >
            <Table className="w-4 h-4" />
          </button>

          {/* Plus Insert Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setInsertOpen(!insertOpen)}
              className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
              title="More Inserts"
            >
              <Plus className="w-4 h-4" />
            </button>
            {insertOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 w-44 rounded-md border bg-popover p-1 shadow-lg text-xs space-y-1" onMouseLeave={() => setInsertOpen(false)}>
                <button type="button" onClick={() => { insertTable(3, 3); setInsertOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-purple-600 dark:text-purple-400 font-medium">
                  <Table className="w-3.5 h-3.5" /> Insert Table
                </button>
                <button type="button" onClick={() => { fileInputRef.current?.click(); setInsertOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium">
                  <ImageIcon className="w-3.5 h-3.5" /> Upload Image
                </button>
                <button type="button" onClick={() => { insertCallout("info"); setInsertOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-blue-500">
                  <Info className="w-3.5 h-3.5" /> Info Panel
                </button>
                <button type="button" onClick={() => { insertCallout("warning"); setInsertOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-amber-500">
                  <AlertCircle className="w-3.5 h-3.5" /> Warning Panel
                </button>
                <button type="button" onClick={() => { insertCallout("success"); setInsertOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-emerald-500">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Success Panel
                </button>
                <button type="button" onClick={() => { execCmd("formatBlock", "pre"); setInsertOpen(false); }} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2">
                  <Code className="w-3.5 h-3.5" /> Code Block
                </button>
              </div>
            )}
          </div>

          {/* @ Mention Teammate */}
          <button
            type="button"
            onClick={() => {
              insertHtmlAtCursor("@");
              setTimeout(() => checkMentionTrigger(), 10);
            }}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors text-primary"
            title="Mention a teammate (@)"
            data-testid="editor-mention-btn"
          >
            <AtSign className="w-4 h-4" />
          </button>

          {/* Link */}
          <button
            type="button"
            onClick={insertLink}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
            title="Insert Link"
          >
            <LinkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Undo / Redo */}
        <div className="flex items-center gap-0.5 text-muted-foreground">
          <button
            type="button"
            onClick={() => execCmd("undo")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
            title="Undo (Ctrl+Z)"
          >
            <Undo className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => execCmd("redo")}
            className="p-1.5 rounded-md hover:bg-accent hover:text-foreground transition-colors"
            title="Redo (Ctrl+Y)"
          >
            <Redo className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* True WYSIWYG contentEditable Canvas */}
      <div className="p-4 relative min-h-[160px] bg-background">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onFocus={() => setIsFocused(true)}
          onBlur={() => { setIsFocused(false); emitChange(); }}
          onInput={(e) => { emitChange(); checkMentionTrigger(); }}
          onKeyUp={checkMentionTrigger}
          onKeyDown={handleEditorKeyDown}
          onPaste={handlePaste}
          className="outline-none min-h-[140px] text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none focus:outline-none"
        />

        {/* @ Mention Autocomplete Popover */}
        {mentionOpen && mentionMatches.length > 0 && (
          <div
            className="absolute z-50 w-72 rounded-lg border border-border bg-popover shadow-xl overflow-hidden text-popover-foreground transition-all duration-100"
            style={{
              top: Math.min(Math.max(10, mentionCoords.top), 240),
              left: Math.min(Math.max(10, mentionCoords.left), 320),
            }}
            data-testid="mention-autocomplete-menu"
          >
            <div className="px-3 py-1.5 bg-muted/60 border-b text-[10px] uppercase font-bold tracking-wider text-muted-foreground flex items-center gap-1.5">
              <AtSign className="w-3 h-3 text-primary" />
              <span>Mention Teammate</span>
            </div>
            <div className="max-h-56 overflow-y-auto p-1 divide-y divide-border/20">
              {mentionMatches.map((u, idx) => (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMentionUser(u);
                  }}
                  onMouseEnter={() => setMentionActiveIdx(idx)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2.5 py-2 text-left rounded-md transition-colors text-xs",
                    idx === mentionActiveIdx ? "bg-accent text-accent-foreground font-medium" : "hover:bg-accent/50 text-foreground"
                  )}
                  data-testid={`mention-user-${u.id}`}
                >
                  <Avatar className="w-6 h-6 shrink-0">
                    <AvatarImage src={u.avatar_url} />
                    <AvatarFallback className="text-[9px]">{initials(u.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{u.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                  </div>
                  <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                    {u.role || "user"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Drag & Drop Visual Overlay */}
        {dragActive && (
          <div className="absolute inset-0 z-20 bg-primary/10 border-2 border-dashed border-primary rounded-md flex flex-col items-center justify-center p-4 backdrop-blur-xs pointer-events-none">
            <ImageIcon className="w-8 h-8 text-primary animate-bounce mb-1" />
            <span className="text-xs font-semibold text-primary">Drop image to upload directly into description</span>
          </div>
        )}
      </div>
    </div>
  );
}
